/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import axios from 'axios';
import { useAuthStore } from '@store/auth.store';
import { logClientError } from '@/lib/clientLog';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/**
 * Per-request options for posting a `FormData` body through `api`.
 *
 * Both fields are load-bearing. The instance default above is
 * `application/json`, and axios's `transformRequest` JSON-stringifies a
 * `FormData` body whenever the content type says JSON, so an upload without
 * this override reaches the server as `{"file":{}}`. Naming multipart makes
 * axios pass the `FormData` through untouched and then drop the header so the
 * browser sets the boundary. And `timeout: 0` lifts the 30 s default: the
 * package uploads accept files up to 300 MB, and the `fetch` calls these
 * options replaced never timed out.
 */
export const FORM_DATA_REQUEST = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 0,
} as const;

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Log every failed request in one place, then handle auth errors globally.
// Callers still receive the rejection and decide what the screen shows; what
// they can no longer do is make the failure invisible (survey 03 #15).
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const method = (error.config?.method ?? 'request').toUpperCase();
    const url = error.config?.url ?? '(unknown url)';
    logClientError(`${method} ${url}`, error);
    if (error.response?.status === 401) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
